import keys from '@debbie/common/dist/cache/keys.js';
import { CreateContactSchema, } from '@debbie/common/dist/contacts/index.js';
import { BankAccountType, phoneToString, } from '@debbie/common/dist/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as contactsAPI from '@debbie/cortex/dist/api/contacts.js';
import { contactsEntity, contactsKey, useContacts, } from '@debbie/cortex/dist/contacts/index.js';
import { useIsAdmin } from '@debbie/cortex/dist/me/index.js';
import * as notifications from '@debbie/cortex/dist/notifications.js';
import q from '@debbie/cortex/dist/query.js';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { errorNotification, successNotification } from '../utils/errors.js';
import Button from './Button.js';
import BankAccountInput from './economy/BankAccountInput.js';
import IconButton from './IconButton.js';
import { Edit2, PlusCircle, Trash2, User } from './Icons/index.js';
import Input from './Input.js';
import Modal from './Modal.js';
import PhoneInput from './PhoneInput.js';
import RegistryIdentifierInput from './RegistryIdentifierInput.js';
import { PALETTE, storeRecent } from './SearchableDropdown.js';
import SearchableSelect from './SearchableSelect.js';
import SmallHeader from './SmallHeader.js';
const RECENTS_STORAGE_KEY = 'debbie.contactSelect.recentlyUsed';
const CONTACT_ICON = { icon: React.createElement(User, null), palette: PALETTE.neutral };
const contactId = (contact) => contact.id;
const contactName = (contact) => contact.name;
const contactIcon = () => CONTACT_ICON;
const contactSearchText = (contact) => `${contact.name} ${contact.email ?? ''} ${contact.phone?.number ?? ''}`;
const contactDescription = (contact) => [
    contact.email,
    phoneToString(contact.phone) || null,
    contact.registryIdentifier?.value,
    contact.bankAccount != null
        ? `${contact.bankAccount.registrationNumber}-${contact.bankAccount.accountNumber}`
        : null,
]
    .filter((part) => part != null)
    .join(' · ');
/** Stable filters for the contexts a contact is picked in */
export const hasEmail = (contact) => contact.email != null;
export const hasBankAccount = (contact) => contact.bankAccount != null;
/**
 * Searchable single-select for the tenant's contacts (suppliers such as a
 * locksmith hired on behalf of the creditor). Admins create contacts while
 * searching and edit or delete them from the rows; everyone else only picks.
 */
const ContactSelect = (props) => {
    const lang = useSelector((s) => s.lang);
    const isAdmin = useIsAdmin();
    const contacts = useContacts();
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const deleteMutation = useMutation(async (contact) => {
        await contactsAPI.remove(contact.id);
        q.invalidate(keys.write.modify(contactsEntity, contact.id));
        return contact;
    }, {
        onSuccess: (contact) => {
            successNotification(lang, 'delete', lang.contacts.contact);
            setDeleting(null);
            if (props.value === contact.id) {
                props.onChange(null, null);
            }
        },
        onError: (err) => errorNotification(lang, err, 'delete', lang.contacts.contact),
    });
    // Also offered while browsing, so an empty list can be filled from here
    const createRow = (search) => ({
        icon: React.createElement(PlusCircle, null),
        label: lang.contacts.createContact,
        description: search === '' ? undefined : `“${search}”`,
        onSelect: () => setEditing({ name: search }),
    });
    const rowActions = (contact) => (React.createElement(React.Fragment, null,
        React.createElement(IconButton, { type: "button", title: lang.contacts.editContact, "aria-label": lang.contacts.editContact, onClick: () => setEditing({ contact }) },
            React.createElement(Edit2, { size: "14px" })),
        React.createElement(IconButton, { type: "button", $variant: "danger", title: lang.contacts.deleteContact, "aria-label": lang.contacts.deleteContact, onClick: () => setDeleting(contact) },
            React.createElement(Trash2, { size: "14px" }))));
    return (React.createElement(React.Fragment, null,
        React.createElement(SearchableSelect, { ...props, filter: isAdmin === true ? undefined : props.filter, isItemSelectable: props.filter, placeholder: props.placeholder ?? lang.contacts.selectContact, busy: deleteMutation.isLoading, items: contacts, getId: contactId, getLabel: contactName, getSearchText: contactSearchText, getIcon: contactIcon, getDescription: contactDescription, getRowActions: isAdmin === true ? rowActions : undefined, extraRow: isAdmin === true ? createRow : undefined, labels: {
                search: lang.contacts.searchContacts,
                all: lang.contacts.allContacts,
            }, recentsStorageKey: RECENTS_STORAGE_KEY }),
        deleting != null && (React.createElement(Modal, { header: lang.contacts.deleteContact, onToggle: () => setDeleting(null), maxWidth: "400px" },
            React.createElement("p", null,
                lang.contacts.deleteContactConfirm,
                " ",
                React.createElement("b", null, deleting.name)),
            React.createElement(Button, { block: true, theme: "dangerous", loading: deleteMutation.isLoading, onClick: () => void deleteMutation.mutate(deleting) }, lang.contacts.deleteContact),
            React.createElement(Button, { block: true, theme: "light", style: { marginTop: '10px' }, onClick: () => setDeleting(null) }, lang.misc.cancel))),
        editing != null && (React.createElement(ContactEditor, { editing: editing, onClose: () => setEditing(null), onSaved: (contact, isNew) => {
                setEditing(null);
                if (!isNew && props.value !== contact.id) {
                    return;
                }
                // A contact the caller's filter rejects, e.g. one without a bank
                // account, is not selectable. An edited selection is re-emitted
                // so the parent sees the new fields
                const selectable = props.filter == null || props.filter(contact);
                if (!selectable) {
                    notifications.notification({
                        title: lang.contacts.notSelectableHere,
                        theme: 'warning',
                    });
                    props.onChange(null, null);
                    return;
                }
                if (isNew) {
                    storeRecent(RECENTS_STORAGE_KEY, contact.id);
                }
                props.onChange(contact.id, contact);
            } }))));
};
export default ContactSelect;
const draftOf = (editing) => 'contact' in editing
    ? {
        name: editing.contact.name,
        email: editing.contact.email ?? '',
        phone: editing.contact.phone,
        registryIdentifier: editing.contact.registryIdentifier,
        bankAccount: [
            editing.contact.bankAccount?.registrationNumber ?? '',
            editing.contact.bankAccount?.accountNumber ?? '',
        ],
    }
    : {
        name: editing.name,
        email: '',
        phone: null,
        registryIdentifier: null,
        bankAccount: ['', ''],
    };
const toNewContact = (draft) => {
    const [registrationNumber, accountNumber] = draft.bankAccount;
    return {
        name: draft.name.trim(),
        email: draft.email.trim() || null,
        phone: draft.phone,
        registryIdentifier: draft.registryIdentifier == null ||
            draft.registryIdentifier.value.trim() === ''
            ? null
            : draft.registryIdentifier,
        bankAccount: registrationNumber === '' && accountNumber === ''
            ? null
            : { type: BankAccountType.Danish, registrationNumber, accountNumber },
    };
};
const ContactEditor = (props) => {
    const lang = useSelector((s) => s.lang);
    const existing = 'contact' in props.editing ? props.editing.contact : null;
    const [draft, setDraft] = useState(() => draftOf(props.editing));
    const parsed = CreateContactSchema.safeParse(toNewContact(draft));
    const saveMutation = useMutation(async (newContact) => {
        if (existing == null) {
            const contact = await contactsAPI.create(newContact);
            // Selectable right away, before the invalidated collection reloads
            q.mutateCache(contactsKey, (all) => [...all, contact]);
            q.invalidate(keys.write.create(contactsEntity));
            return contact;
        }
        const contact = await contactsAPI.update(existing.id, newContact);
        q.invalidate(keys.write.modify(contactsEntity, contact.id));
        return contact;
    }, {
        onSuccess: (contact) => {
            successNotification(lang, existing == null ? 'create' : 'update', lang.contacts.contact);
            props.onSaved(contact, existing == null);
        },
        onError: (err) => errorNotification(lang, err, existing == null ? 'create' : 'update', lang.contacts.contact),
    });
    const field = (key) => ({
        value: draft[key],
        onChange: (e) => setDraft({ ...draft, [key]: e.target.value }),
    });
    return (React.createElement(Modal, { header: existing == null
            ? lang.contacts.createContact
            : lang.contacts.editContact, onToggle: props.onClose, maxWidth: "440px" },
        React.createElement(Input, { placeholder: lang.misc.name, autofocus: true, ...field('name') }),
        React.createElement(Input, { placeholder: lang.contacts.email, type: "email", ...field('email') }),
        React.createElement(PhoneInput, { phone: draft.phone, placeholder: lang.contacts.phone, onChange: (phone) => setDraft({ ...draft, phone }) }),
        React.createElement(SmallHeader, null, lang.contacts.registryIdentifier),
        React.createElement(RegistryIdentifierInput, { value: draft.registryIdentifier, onChange: (registryIdentifier) => setDraft({ ...draft, registryIdentifier }) }),
        React.createElement(SmallHeader, null, lang.contacts.bankAccount),
        React.createElement(BankAccountInput, { value: draft.bankAccount, onChange: (bankAccount) => setDraft({ ...draft, bankAccount }) }),
        React.createElement(Button, { block: true, disabled: !parsed.success, loading: saveMutation.isLoading, onClick: () => {
                if (parsed.success) {
                    void saveMutation.mutate(parsed.data);
                }
            } }, existing == null ? lang.misc.create : lang.misc.save)));
};
//# sourceMappingURL=ContactSelect.js.map